import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Search, Menu, X, User, LogOut, Package, ShoppingCart,
  MessageSquare, ChevronDown, Shield, FileText, Store, Crown, Bell, Settings
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { channelApi } from '../../services/api';

const NAV_LINKS = [
  { to: '/channel', label: 'Premium Channel', testId: 'nav-channel', accent: true },
  { to: '/products', label: 'Marketplace', testId: 'nav-products' },
  { to: '/suppliers', label: 'Suppliers', testId: 'nav-suppliers' },
  { to: '/rfqs', label: 'Buy Requests', testId: 'nav-rfqs' },
  { to: '/membership', label: 'Membership', testId: 'nav-membership' },
];

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setShowNotifs(false);
  }, [location.pathname]);

  // Poll notification count for authenticated users
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchCount = async () => {
      try {
        const { data } = await channelApi.getNotificationCount();
        setUnreadCount(data.unread_count || 0);
      } catch (e) { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000); // every 30s
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const openNotifDropdown = async () => {
    setShowNotifs(!showNotifs);
    if (!showNotifs) {
      try {
        const { data } = await channelApi.getNotifications({ limit: 8 });
        setNotifications(data.notifications || []);
      } catch (e) { /* ignore */ }
    }
  };

  const handleMarkRead = async (notifId) => {
    try {
      await channelApi.markRead(notifId);
      setNotifications(prev => prev.map(n => n.notif_id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await channelApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) { /* ignore */ }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    if (user.role === 'admin') return '/admin';
    if (user.user_type === 'agent') return '/dashboard/agent';
    if (user.user_type === 'supplier') return '/dashboard/supplier';
    return '/dashboard/buyer';
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const isActive = (path) => location.pathname.startsWith(path);

  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-shadow duration-200 ${
        scrolled ? 'shadow-md' : 'border-b border-slate-200'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-[64px] lg:h-[72px] gap-4">

          {/* ─── LEFT: Logo ─── */}
          <Link to="/" className="flex items-center shrink-0" data-testid="header-logo">
            <span className="font-extrabold text-[#0A192F] text-[20px] tracking-tight">eaglesmarket.com</span>
          </Link>

          {/* ─── CENTER: Navigation ─── */}
          <nav className="hidden lg:flex items-center gap-1 ml-8" data-testid="desktop-nav">
            {NAV_LINKS.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`relative px-3.5 py-2 text-[13px] font-semibold transition-colors rounded-md ${
                  isActive(link.to)
                    ? link.accent ? 'text-[#D4AF37] bg-amber-50/60' : 'text-[#0A192F] bg-slate-50'
                    : link.accent ? 'text-[#D4AF37] hover:bg-amber-50/40' : 'text-slate-500 hover:text-[#0A192F] hover:bg-slate-50/60'
                }`}
                data-testid={link.testId}
              >
                {link.accent && <Crown className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
                {link.label}
                {isActive(link.to) && (
                  <span className={`absolute bottom-0 left-3.5 right-3.5 h-[2px] rounded-full ${link.accent ? 'bg-[#D4AF37]' : 'bg-[#EA580C]'}`} />
                )}
              </Link>
            ))}
          </nav>

          {/* ─── Spacer ─── */}
          <div className="flex-1" />

          {/* ─── RIGHT: Search + Actions ─── */}
          <div className="flex items-center gap-2.5">

            {/* Search Input - Desktop */}
            <form onSubmit={handleSearch} className="hidden xl:flex relative" data-testid="header-search-form">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products, suppliers..."
                className="w-[240px] h-9 pl-9 pr-3 text-sm bg-slate-50 border border-slate-200 rounded-md text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0A192F]/10 focus:border-slate-300 transition-all"
                data-testid="header-search-input"
              />
            </form>

            {/* Search Icon - Tablet */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/search')}
              className="xl:hidden hidden sm:flex text-slate-500 hover:text-[#0A192F] h-9 w-9"
              data-testid="header-search-btn"
            >
              <Search className="w-[18px] h-[18px]" />
            </Button>

            {isAuthenticated ? (
              <>
                {/* Post Requirement - Authenticated */}
                <Button
                  onClick={() => navigate('/rfqs/create')}
                  className="hidden md:flex bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-md h-9 px-4 text-[13px] font-semibold shadow-sm shadow-orange-200/50"
                  data-testid="header-post-rfq-btn"
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5" />
                  Post Requirement
                </Button>

                {/* Notification Bell */}
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={openNotifDropdown}
                    className="relative h-9 w-9 text-slate-500 hover:text-[#0A192F]"
                    data-testid="notification-bell"
                  >
                    <Bell className="w-[18px] h-[18px]" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-[18px] h-[18px] bg-[#EA580C] text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>

                  {/* Notification Dropdown */}
                  {showNotifs && (
                    <div className="absolute right-0 top-11 w-96 bg-white border border-slate-200 shadow-xl rounded-lg z-50 overflow-hidden" data-testid="notification-dropdown">
                      <div className="px-4 py-3 border-b flex items-center justify-between bg-slate-50">
                        <h3 className="font-semibold text-sm text-[#0A192F]">Notifications</h3>
                        <div className="flex items-center gap-2">
                          {unreadCount > 0 && (
                            <button onClick={handleMarkAllRead} className="text-xs text-[#EA580C] font-medium hover:underline" data-testid="mark-all-read-btn">
                              Mark all read
                            </button>
                          )}
                          <button onClick={() => { navigate('/notifications/settings'); setShowNotifs(false); }} className="text-slate-400 hover:text-slate-600">
                            <Settings className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto divide-y">
                        {notifications.length === 0 ? (
                          <div className="px-4 py-8 text-center">
                            <Bell className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                            <p className="text-sm text-slate-400">No notifications yet</p>
                          </div>
                        ) : (
                          notifications.map(n => (
                            <button
                              key={n.notif_id}
                              onClick={() => { handleMarkRead(n.notif_id); navigate(`/channel/${n.post_id}`); setShowNotifs(false); }}
                              className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${!n.is_read ? 'bg-blue-50/40' : ''}`}
                              data-testid={`notif-${n.notif_id}`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${!n.is_read ? 'bg-[#EA580C]' : 'bg-transparent'}`} />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 mb-0.5">
                                    {n.badge && (
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                                        n.badge === 'High Value' ? 'bg-green-100 text-green-800' :
                                        n.badge === 'Hot Deal' ? 'bg-red-100 text-red-700' :
                                        n.badge === 'Priority' ? 'bg-amber-100 text-amber-800' :
                                        n.badge === 'Limited' ? 'bg-purple-100 text-purple-700' :
                                        n.badge === 'Verified' ? 'bg-blue-100 text-blue-700' :
                                        'bg-slate-100 text-slate-600'
                                      }`} data-testid={`notif-badge-${n.notif_id}`}>
                                        {n.badge}
                                      </span>
                                    )}
                                    {n.deal_value && <span className="text-[10px] font-bold text-green-700">{n.deal_value}</span>}
                                  </div>
                                  <p className="text-sm font-medium text-slate-800 truncate">{n.title}</p>
                                  <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{n.teaser}</p>
                                  <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                                </div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                      <button
                        onClick={() => { navigate('/notifications'); setShowNotifs(false); }}
                        className="w-full px-4 py-2.5 text-center text-xs font-semibold text-[#EA580C] hover:bg-slate-50 border-t"
                        data-testid="view-all-notifications"
                      >
                        View All Notifications
                      </button>
                    </div>
                  )}
                </div>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 h-9 px-2" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-[#0A192F] flex items-center justify-center text-white text-xs font-bold overflow-hidden">
                        {user?.picture ? (
                          <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          user?.name?.charAt(0) || 'U'
                        )}
                      </div>
                      <span className="hidden lg:block text-sm font-medium text-slate-700 max-w-[100px] truncate">{user?.name?.split(' ')[0]}</span>
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2.5 border-b">
                      <p className="text-sm font-semibold text-[#0A192F]">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                      {user?.membership && user.membership !== 'free' && (
                        <span className="inline-flex items-center mt-1.5 px-2 py-0.5 rounded-sm text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wider">
                          <Shield className="w-3 h-3 mr-1" />
                          {user.membership}
                        </span>
                      )}
                    </div>
                    <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="menu-dashboard">
                      <Package className="w-4 h-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                    {user?.role === 'admin' && (
                      <DropdownMenuItem onClick={() => navigate('/admin/channel')} data-testid="menu-channel-admin">
                        <Crown className="w-4 h-4 mr-2" /> Channel Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/dashboard/messages')} data-testid="menu-messages">
                      <MessageSquare className="w-4 h-4 mr-2" /> Messages
                    </DropdownMenuItem>
                    {user?.user_type === 'supplier' && (
                      <DropdownMenuItem onClick={() => navigate('/dashboard/products/add')} data-testid="menu-add-product">
                        <ShoppingCart className="w-4 h-4 mr-2" /> Add Product
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                {/* Sign In */}
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="hidden md:flex text-[13px] font-semibold text-slate-600 hover:text-[#0A192F] h-9 px-3"
                  data-testid="header-login-btn"
                >
                  Sign In
                </Button>

                {/* Post Requirement - Orange CTA */}
                <Button
                  onClick={() => navigate('/register?type=buyer')}
                  className="hidden sm:flex bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-md h-9 px-4 text-[13px] font-semibold shadow-sm shadow-orange-200/50"
                  data-testid="header-post-requirement-btn"
                >
                  Post Requirement
                </Button>

                {/* Join as Supplier - Blue CTA */}
                <Button
                  onClick={() => navigate('/register?type=supplier')}
                  className="hidden lg:flex bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-md h-9 px-4 text-[13px] font-semibold shadow-sm shadow-blue-200/50"
                  data-testid="header-join-supplier-btn"
                >
                  <Store className="w-3.5 h-3.5 mr-1.5" />
                  Join as Supplier
                </Button>
              </>
            )}

            {/* Hamburger */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden h-9 w-9"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* ─── Mobile Drawer ─── */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 animate-fade-in" data-testid="mobile-menu">
          {/* Mobile Search */}
          <form onSubmit={handleSearch} className="px-4 pt-3 pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products, suppliers..."
                className="w-full h-10 pl-9 pr-3 text-sm bg-slate-50 border border-slate-200 rounded-md text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0A192F]/10"
                data-testid="mobile-search-input"
              />
            </div>
          </form>

          {/* Mobile Nav Links */}
          <nav className="px-2 pb-2">
            {NAV_LINKS.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                  isActive(link.to)
                    ? 'text-[#0A192F] bg-slate-50 font-semibold'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
                {isActive(link.to) && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#EA580C]" />}
              </Link>
            ))}
          </nav>

          {/* Mobile CTAs */}
          <div className="px-4 pb-4 space-y-2 border-t border-slate-100 pt-3">
            {isAuthenticated ? (
              <Button
                onClick={() => { navigate('/rfqs/create'); setMobileMenuOpen(false); }}
                className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-md h-10 text-sm font-semibold"
                data-testid="mobile-post-requirement"
              >
                <FileText className="w-4 h-4 mr-2" />
                Post Requirement
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => { navigate('/register?type=buyer'); setMobileMenuOpen(false); }}
                  className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-md h-10 text-sm font-semibold"
                  data-testid="mobile-post-requirement"
                >
                  Post Requirement
                </Button>
                <Button
                  onClick={() => { navigate('/register?type=supplier'); setMobileMenuOpen(false); }}
                  className="w-full bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-md h-10 text-sm font-semibold"
                  data-testid="mobile-join-supplier"
                >
                  <Store className="w-4 h-4 mr-2" />
                  Join as Supplier
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                  className="w-full rounded-md h-10 text-sm font-semibold"
                  data-testid="mobile-sign-in"
                >
                  Sign In
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
